import { Phone } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { TaskCard } from "../types";
import { formatDayMonth, formatTime } from "./cardStatus";
import { fullName } from "./hooks";
import { useTaskActions } from "./taskActions";
import { taskTiming } from "./todayLogic";

const badgeClass = {
  overdue: "bg-destructive/10 text-destructive",
  today: "bg-honey/20 text-warning",
  later: "bg-accent text-primary",
  none: "bg-muted text-muted-foreground",
};

/**
 * A task in "Сьогодні" and the tasks page (docs/design/Main, TasksM).
 * Ticking it plays a short chord, flashes green and lets the row slide away.
 */
export const TaskRow = ({
  task,
  quickActions = true,
}: {
  task: TaskCard;
  quickActions?: boolean;
}) => {
  const translate = useTranslate();
  const actions = useTaskActions();
  const { taskTypes } = useConfigurationContext();
  const [finished, setFinished] = useState(false);
  const now = new Date();
  const timing = taskTiming(task.due_date, now);
  const due = task.due_date ? new Date(task.due_date) : null;
  const title =
    task.text ||
    taskTypes.find((type) => type.value === task.type)?.label ||
    "";
  const badge =
    timing === "overdue"
      ? due &&
        now.getTime() - due.getTime() < 2 * 24 * 3600_000 &&
        due.getDate() !== now.getDate()
        ? translate("crm.today.yesterday")
        : translate("crm.today.overdue_badge")
      : timing === "today" && due
        ? formatTime(due)
        : timing === "later" && due
          ? formatDayMonth(due)
          : null;

  const finish = async () => {
    setFinished(true);
    const ok = await actions.done(task);
    if (!ok) setFinished(false);
  };

  const body = (
    <>
      <div
        className={cn(
          "font-bold leading-tight transition-colors",
          finished && "text-muted-foreground line-through",
        )}
      >
        {title}
      </div>
      <div className="truncate text-sm text-muted-foreground">
        {fullName(task)}
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "group flex items-center gap-3 rounded-2xl border bg-card p-4 transition-all duration-500",
        timing === "overdue" &&
          !finished &&
          "border-destructive/40 motion-safe:animate-[pulse-border_2.4s_ease-in-out_infinite]",
        finished &&
          "scale-[0.98] border-[#8fc5a3] bg-[#e3f1e8] opacity-0 dark:bg-[#1c3a29]",
      )}
      style={finished ? { transitionDelay: "450ms" } : undefined}
    >
      <button
        type="button"
        aria-label={translate("crm.tasks_page.action_done")}
        onClick={finish}
        disabled={finished}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full border-2 transition-all",
          finished
            ? "scale-110 border-[#2f7a50] bg-[#2f7a50] text-white"
            : "border-muted-foreground/40 hover:border-[#2f7a50] hover:bg-[#e3f1e8]",
        )}
      >
        <svg
          viewBox="0 0 24 24"
          className={cn(
            "size-5 transition-opacity",
            finished ? "opacity-100" : "opacity-0 group-hover:opacity-40",
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </button>
      {task.deal_id ? (
        <Link
          to={`/funnel/${task.deal_id}`}
          className="min-w-0 flex-1 text-foreground no-underline"
        >
          {body}
        </Link>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      {quickActions && !finished ? (
        <div className="hidden items-center gap-1 sm:flex">
          {task.phone ? (
            <a
              href={`tel:${task.phone}`}
              aria-label={translate("crm.funnel.lead.call")}
              className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"
            >
              <Phone className="size-4" />
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => actions.later(task, 60)}
            className="rounded-lg border px-2 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            +1 {translate("crm.tasks_page.hour")}
          </button>
          <button
            type="button"
            onClick={() => actions.tomorrow(task)}
            className="rounded-lg border px-2 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            {translate("crm.tasks_page.tomorrow")}
          </button>
        </div>
      ) : null}
      {badge && !finished ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-bold",
            badgeClass[timing],
          )}
        >
          {badge}
        </span>
      ) : null}
    </div>
  );
};
